import { h } from 'preact';
import Store from 'store/store';
import { getMovieLocales } from 'utils/baseMovies';
import { isBaseRegion, isFocusStudio } from 'utils/studioConfig';
import { getImageDimensions, calcImagePositionForFocalPoint } from 'utils/media';

import Button from 'components/button/button';
import List from 'components/list/list';
import AddMovieButton from 'components/movies/addMovieButton';
import WithTooltip from 'components/tooltip/withTooltip';

import s from 'components/list/movieList.sss';

const FOCUS_HERO_DEFAULT = `${__IMAGE_ROOT__}/heros/focus-hero-default.jpg`;
const UNIVERSAL_HERO_DEFAULT = `${__IMAGE_ROOT__}/heros/universal-hero-default.png`;

export default class MovieList extends List {
	s = s;

	get isFocus() {
		return isFocusStudio();
	}

	get isBase() {
		return isBaseRegion();
	}

	get itemsSource() {
		return this.props.data?.movies || {};
	}

	get attributes() {
		const base = super.attributes;

		if (this.isBase) {
			return {
				name: base.name,
				lastModified: base.lastModified,
				regions: { name: 'Regions', type: 'regions', accessor: this.getItemLocaleLabels, render: this.renderListItemRegions },
			};
		}

		return {
			...base,
			releaseDate: { name: 'Release Date', type: 'date', accessor: this.getItemReleaseDate, default: 'Unknown', defaultHidden: true },
			showtimesStatus: { name: 'Release', accessor: this.getShowtimesStatusText, render: this.renderShowtimesStatusText },
		};
	}

	get availableFilters() {
		if (this.isBase) return null;

		return {
			draftPlaceholder: { name: 'Placeholder - Draft', run: item => this.isPlaceholder(item) && !this.isLive(item) },
			livePlaceholder: { name: 'Placeholder - Live', run: item => this.isPlaceholder(item) && this.isLive(item) },
			liveMovie: { name: 'Movie - Live', run: item => !this.isPlaceholder(item) && this.isLive(item) },
		};
	}

	get sortOptions() {
		return ['lastModified', !this.isBase && 'lastPublished', 'name', !this.isBase && 'releaseDate'].filter(Boolean);
	}

	constructor(props) {
		super(props);

		// Pre-calculate background positions for all items to optimize rendering
		this.setBackgroundPositions(this.getItems());

		this.getShowtimesStatusText = this.getShowtimesStatusText.bind(this);
		this.getItemLocaleLabels = this.getItemLocaleLabels.bind(this);
		this.renderListItemRegions = this.renderListItemRegions.bind(this);
	}

	getItemReleaseDate(item) {
		const date = new Date(item.content?.dates?.release);
		return !isNaN(date) && date;
	}

	getTitle() {
		return 'Movies';
	}

	getDescription() {
		return 'Edit information and media related to movies';
	}

	isPlaceholder(item) {
		// return isPlaceholder(item);
		// We want to check if the currently live site is a placeholder, not the draft data (which is what the isPlaceholder util currently checks)
		return item?.published_status?.endsWith('_PLACEHOLDER');
	}

	isLive(item) {
		return !!item?.commits?.publish;
	}

	getItemName(item) {
		return item?.base_movie?.content?.title || item?.content?.title;
	}

	getItemLocaleLabels(item) {
		const locales = getMovieLocales(item.id)?.locales || [];
		const liveLocales = locales.filter(locale => this.isLive(locale?.localised_movie)).map(locale => locale.code.toUpperCase());
		const plannedLocales = locales.filter(locale => !this.isLive(locale?.localised_movie)).map(locale => locale.code.toUpperCase());

		const liveCount = liveLocales.length;
		const plannedCount = plannedLocales.length;

		const liveLabel = liveCount > 0
			? (
				liveCount === 1
					? `1 live region (${liveLocales[0]})`
					: `${liveCount} live regions`
			) : null;

		const plannedLabel = plannedCount > 0
			? (
				plannedCount === 1
					? `1 planned region (${plannedLocales[0]})`
					: `${plannedCount} planned regions`
			) : null;

		const liveTooltip = liveCount > 0 ? liveLocales.join(', ') : 'None';
		const plannedTooltip = plannedCount > 0 ? plannedLocales.join(', ') : 'None';

		return { liveLabel, liveTooltip, plannedLabel, plannedTooltip };
	}

	getHeroImage(item) {
		if (this.isFocus) {
			const focusHero = item?.content?.hero?.still || {};
			return focusHero?.url ? focusHero : { url: FOCUS_HERO_DEFAULT };
		}

		const localeHero = item?.content?.hero?.desktop?.still || {};
		const baseHero = item?.base_movie?.content?.hero?.desktop?.still || {};
		return {
			...baseHero,
			...localeHero,
			url: localeHero?.url || baseHero?.url || UNIVERSAL_HERO_DEFAULT,
		};
	}

	getItemImage(item) {
		return this.getHeroImage(item)?.url || item?.content?.heroImage;
	}

	async setBackgroundPositions(items) {
		const getBackgroundPosition = async (item) => {
			const [containerWidth, containerHeight] = [264, 200]; // fixed size
			const { focalPoint, url } = this.getHeroImage(item) || {};
			const { focalX, focalY } = focalPoint || {};
			const { width, height } = await getImageDimensions(url);
			return calcImagePositionForFocalPoint(width, height, containerWidth, containerHeight, focalX, focalY);
		};

		if (!this.isFocus) return;

		// Get background positions for all items as an array of promises
		const loadAllBackgroundPositions = items
			.filter(item => item.content.hero?.still?.focalPoint)
			.map(async item => [item.id, await getBackgroundPosition(item)]);
		// Wait for all background positions to load
		const backgroundPositionsArray = await Promise.all(loadAllBackgroundPositions);
		// Convert the array of [id, position] pairs into an object
		const backgroundPositions = Object.fromEntries(backgroundPositionsArray);

		this.setState({ backgroundPositions });
	}

	getItemStatus(item) {
		// For now, we cannot publish non-placeholder movies, so mark them as always live
		// if (!this.isPlaceholder(item)) return 'live';
		return item.commits?.publish ? 'live' : 'draft';
	}

	getShowtimesStatusText(item) {
		if (this.isPlaceholder(item)) return null;
		const status = Store.get().movieStatuses?.[item?.id] || {};
		let statusText = [
			status.theaters === 'inTheaters' && 'In theaters',
			status.theaters === 'preSale' && 'Pre-sale',
			status.theaters === 'comingSoon' && 'Coming soon',
			status.homeEnt === 'homeEnt' && 'At home',
		].filter(Boolean).join(' + ');
		return statusText;
	}

	renderShowtimesStatusText(item, value) {
		if (!value) return null;
		return <span class={s.showtimesStatus}>{value}</span>;
	}

	renderRegions(labels, showHeader) {
		if (!labels) return;
		const { liveLabel, liveTooltip, plannedLabel, plannedTooltip } = labels;
		const tooltipContent = (
			<div class={this.s.regionsTooltip}>
				<strong>Live in:</strong>
				{liveTooltip}
				<strong>Planned in:</strong>
				{plannedTooltip}
			</div>
		);

		return (
			<WithTooltip class={this.s.detail} tooltip={tooltipContent} direction="bottom" tipAlign="start">
				{!!showHeader && <div class={this.s.label}>Regions</div>}
				{!liveLabel && !plannedLabel && <span>No regions added</span>}
				{liveLabel && <span>{liveLabel}</span>}
				{plannedLabel && <span>{plannedLabel}</span>}
			</WithTooltip>
		);
	}

	renderListItemRegions(item, value) {
		return (
			<div class={s.regions}>
				{this.renderRegions(value)}
			</div>
		);
	}

	renderGridPreview(item) {
		const src = this.getItemImage(item);
		const style = {
			backgroundImage: src ? `url('${src}')` : '',
			backgroundPosition: this.state.backgroundPositions?.[item.id] || 'center',
		};

		return <div class={s.preview} data-status={this.getShowtimesStatusText(item)} style={style} />;
	}

	getItemLink(item) {
		const movieId = item?.base_movie_id || item?.id;
		return `#movies/${movieId}/info`;
	}

	search(item) {
		if (!this.state.search) return true;
		const name = this.getItemName(item);
		return name.toLowerCase().includes(this.state.search.toLowerCase());
	}

	renderSettingsButtons() {
		if (this.isBase) return null;
		return <AddMovieButton />;
	}

	renderStatus(item) {
		if (this.isBase) return null;

		return (
			<div class={s.status}>
				<div class={s.statusLabel}>{this.isPlaceholder(item) ? 'Placeholder' : 'Movie Platform'}</div>
				{super.renderStatus(item)}
			</div>
		);
	}

	renderGridItemSubName(item) {
		return this.renderStatus(item);
	}

	renderGridItemDetails(item) {
		if (this.isBase) {
			return (
				<div class={this.s.details}>
					<div class={this.s.detail}>
						<div class={this.s.label}>Last modified</div>
						{this.renderTimeInfo(this.getItemModifiedDate(item))}
					</div>
					{this.renderRegions(this.getItemLocaleLabels(item), true)}
				</div>
			);
		}

		return super.renderGridItemDetails(item);
	}
}